import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save } from 'lucide-react';
import { api } from '../api/client';
import { toast } from '../lib/toast';

export default function LlmsTxtForm({
  domainId,
  domainHost,
  showPreview = true,
  onSaved,
}) {
  // Use the real domain in placeholder examples so they read as guidance for
  // *this* site rather than a generic sample.
  const host = domainHost || 'example.com';
  const qc = useQueryClient();
  const llmsTxtQuery = useQuery({
    queryKey: ['llms-txt', domainId],
    queryFn: () => api.get(`/api/domains/${domainId}/llms-txt`).then((r) => r.data),
    enabled: Boolean(domainId),
  });

  const llmsTxtMutation = useMutation({
    mutationFn: (cfg) =>
      api.put(`/api/domains/${domainId}/llms-txt`, cfg).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['llms-txt', domainId] });
      toast.success('llms.txt regenerated');
      onSaved?.();
    },
    onError: (err) => toast.error(err, 'Could not save llms.txt'),
  });

  const [config, setConfig] = useState({
    description: '',
    priorityPages: '',
    excludedPages: '',
  });

  useEffect(() => {
    if (llmsTxtQuery.data?.config) {
      setConfig({
        description: llmsTxtQuery.data.config.description || '',
        priorityPages: (llmsTxtQuery.data.config.priorityPages || []).join('\n'),
        excludedPages: (llmsTxtQuery.data.config.excludedPages || []).join('\n'),
      });
    }
  }, [llmsTxtQuery.data]);

  const save = () =>
    llmsTxtMutation.mutate({
      description: config.description,
      priorityPages: config.priorityPages
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean),
      excludedPages: config.excludedPages
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean),
    });

  return (
    <div
      className={`grid gap-4 ${
        showPreview ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'
      }`}
    >
      <div className="space-y-2">
        <label className="label">Site description</label>
        <p className="text-xs text-ink-400 -mt-1">
          One sentence on what your site is about — AI engines use it to decide when
          to cite you. Leave blank to use your domain&apos;s default description.
        </p>
        <input
          className="input"
          placeholder="e.g. AI search optimization platform for agencies and SaaS teams"
          value={config.description}
          onChange={(e) => setConfig({ ...config, description: e.target.value })}
        />
        <label className="label">Priority pages (one URL per line)</label>
        <p className="text-xs text-ink-400 -mt-1">
          Full URLs you most want AI engines to cite first. We already add your
          top-scoring and most-referenced pages automatically, so list only the ones
          you want to guarantee (up to 20 total).
        </p>
        <textarea
          className="input min-h-[80px] text-xs"
          placeholder={`https://${host}/pricing\nhttps://${host}/blog/ai-seo-guide`}
          value={config.priorityPages}
          onChange={(e) => setConfig({ ...config, priorityPages: e.target.value })}
        />
        <label className="label">Excluded paths (one per line)</label>
        <p className="text-xs text-ink-400 -mt-1">
          Paths AI crawlers should skip — e.g. cart, checkout, account, or thank-you
          pages. Relative paths work best.
        </p>
        <textarea
          className="input min-h-[60px] text-xs"
          placeholder={'/cart\n/checkout\n/account'}
          value={config.excludedPages}
          onChange={(e) => setConfig({ ...config, excludedPages: e.target.value })}
        />
        <button className="btn-primary" onClick={save} disabled={llmsTxtMutation.isPending}>
          <Save className="w-4 h-4" /> Save & regenerate
        </button>
      </div>
      {showPreview && (
        <div className="flex flex-col h-full min-h-0">
          <label className="label">Preview</label>
          <pre className="flex-1 min-h-[200px] text-xs bg-gray-50 border border-gray-100 rounded-xl p-3 overflow-auto whitespace-pre-wrap scroll-soft">
            {llmsTxtQuery.data?.content || '(no content)'}
          </pre>
        </div>
      )}
    </div>
  );
}
